import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import DataTable from '../components/DataTable';
import { Plus, Edit, Trash2, Power, PowerOff, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import PasswordModal from '../components/PasswordModal';
import { verifyAdminPassword } from '../utils/passwordUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ConsumablesPage = () => {
  const [consumables, setConsumables] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    megasoft_code: '',
    cost_price: 0,
    consumable_type: 'ORIGINAL',
    category: 'ΑΝΑΛΩΣΙΜΟ',
    pages_yield: 0,
    cost_per_page: 0
  });
  const [selectedModels, setSelectedModels] = useState([]);

  useEffect(() => {
    fetchData();
  }, [showInactive]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [consumablesRes, catalogRes] = await Promise.all([
        axios.get(`${API_URL}/consumables?include_inactive=${showInactive}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/catalog`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setConsumables(consumablesRes.data);
      setCatalog(catalogRes.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης δεδομένων');
    } finally {
      setLoading(false);
    }
  };

  const calculateCostPerPage = (price, pages) => {
    if (pages > 0) {
      return (price / pages).toFixed(4);
    }
    return 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('⚠️ ΤΟ ΟΝΟΜΑ ΕΙΝΑΙ ΥΠΟΧΡΕΩΤΙΚΟ!');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Calculate cost per page for consumables
      const saveData = {
        ...formData,
        name: formData.name.toUpperCase(),
        pages_yield: formData.category === 'ΑΝΑΛΩΣΙΜΟ' ? formData.pages_yield : 0,
        cost_per_page: formData.category === 'ΑΝΑΛΩΣΙΜΟ' ? 
          parseFloat(calculateCostPerPage(formData.cost_price, formData.pages_yield)) : 0
      };
      
      let consumableId;
      if (editingId) {
        await axios.put(`${API_URL}/consumables/${editingId}`, saveData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        consumableId = editingId;
        toast.success('Το αναλώσιμο ενημερώθηκε επιτυχώς!');
      } else {
        const response = await axios.post(`${API_URL}/consumables`, saveData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        consumableId = response.data.id;
        toast.success('Το αναλώσιμο προστέθηκε επιτυχώς!');
      }
      
      // Update consumable_models relationships
      if (editingId) {
        // For edit: delete removed links and add new ones
        try {
          const token2 = localStorage.getItem('token');
          const existingLinksRes = await axios.get(`${API_URL}/consumables/${consumableId}/models`, {
            headers: { Authorization: `Bearer ${token2}` }
          });
          const existingCatalogIds = existingLinksRes.data.map(m => m.catalog_id);
          
          // Remove links that are no longer selected
          for (const catId of existingCatalogIds) {
            if (!selectedModels.includes(catId)) {
              await axios.delete(`${API_URL}/consumables/${consumableId}/models/${catId}`, {
                headers: { Authorization: `Bearer ${token2}` }
              }).catch(() => {});
            }
          }
          // Add new links that didn't exist
          for (const catId of selectedModels) {
            if (!existingCatalogIds.includes(catId)) {
              await axios.post(`${API_URL}/consumables/${consumableId}/models/${catId}`, {}, {
                headers: { Authorization: `Bearer ${token2}` }
              }).catch(() => {});
            }
          }
        } catch (err) {
          console.error('Error updating model links:', err);
        }
      } else {
        // For new: just create all links
        for (const catalogId of selectedModels) {
          await axios.post(`${API_URL}/consumables/${consumableId}/models/${catalogId}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => {});
        }
      }
      
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα αποθήκευσης');
    }
  };

  const handleEdit = async (consumable) => {
    setPendingAction({ type: 'edit', consumable });
    setShowPasswordModal(true);
  };

  const executeEdit = async (consumable) => {
    setFormData({
      name: consumable.name,
      brand: consumable.brand || '',
      megasoft_code: consumable.megasoft_code || '',
      cost_price: consumable.cost_price,
      consumable_type: consumable.consumable_type,
      category: consumable.category,
      pages_yield: consumable.pages_yield || 0,
      cost_per_page: consumable.cost_per_page || 0
    });
    
    // Fetch linked models for this consumable
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/consumables/${consumable.id}/models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedModels(response.data.map(m => m.catalog_id));
    } catch (error) {
      setSelectedModels([]);
    }
    
    setEditingId(consumable.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setPendingAction({ type: 'delete', id });
    setShowPasswordModal(true);
  };

  const executeDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/consumables/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.action === 'deactivated') {
        toast.warning(response.data.message);
      } else {
        toast.success('Το αναλώσιμο διαγράφηκε επιτυχώς!');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα διαγραφής');
    }
  };

  const handleToggleActive = async (consumable) => {
    setPendingAction({ type: 'toggle', consumable });
    setShowPasswordModal(true);
  };

  const executeToggleActive = async (consumable) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/consumables/${consumable.id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newStatus = response.data.is_active === 1 ? 'ενεργοποιήθηκε' : 'απενεργοποιήθηκε';
      toast.success(`Το αναλώσιμο ${newStatus}!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα αλλαγής κατάστασης');
    }
  };

  const handlePasswordConfirm = async (password) => {
    const isValid = await verifyAdminPassword(password);
    if (isValid) {
      setShowPasswordModal(false);
      if (pendingAction?.type === 'edit') {
        await executeEdit(pendingAction.consumable);
      } else if (pendingAction?.type === 'delete') {
        await executeDelete(pendingAction.id);
      } else if (pendingAction?.type === 'toggle') {
        await executeToggleActive(pendingAction.consumable);
      }
      setPendingAction(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      megasoft_code: '',
      cost_price: 0,
      consumable_type: 'ORIGINAL',
      category: 'ΑΝΑΛΩΣΙΜΟ',
      pages_yield: 0,
      cost_per_page: 0
    });
    setSelectedModels([]);
    setEditingId(null);
    setShowForm(false);
  };

  const toggleModelSelection = (catalogId) => {
    setSelectedModels(prev => 
      prev.includes(catalogId) 
        ? prev.filter(id => id !== catalogId)
        : [...prev, catalogId]
    );
  };

  const columns = [
    { key: 'name', label: 'ΟΝΟΜΑ', render: (item) => (
      <span className={item.is_active === 0 ? 'text-gray-400 line-through' : ''}>
        {item.name}
        {item.is_active === 0 && <span className="ml-2 text-xs text-red-500">(ΑΝΕΝΕΡΓΟ)</span>}
      </span>
    )},
    { key: 'brand', label: 'ΜΑΡΚΑ', render: (item) => item.brand || '-' },
    { key: 'megasoft_code', label: 'ΚΩΔΙΚΟΣ ΕΙΔΟΥΣ' },
    { key: 'category', label: 'ΚΑΤΗΓΟΡΙΑ' },
    { key: 'consumable_type', label: 'ΤΥΠΟΣ' },
    { 
      key: 'cost_price', 
      label: 'ΤΙΜΗ ΚΟΣΤΟΥΣ',
      render: (item) => `${item.cost_price.toFixed(2)} €`
    },
    {
      key: 'pages_yield',
      label: 'ΣΕΛΙΔΕΣ',
      render: (item) => item.category === 'ΑΝΑΛΩΣΙΜΟ' ? item.pages_yield : '-'
    },
    {
      key: 'cost_per_page',
      label: 'ΚΟΣΤΟΣ/ΣΕΛ',
      render: (item) => item.category === 'ΑΝΑΛΩΣΙΜΟ' ? `${item.cost_per_page.toFixed(4)} €` : '-'
    }
  ];

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-xl sm:text-3xl font-bold">Αναλώσιμα & Ανταλλακτικά</h1>
        <div className="flex gap-2">
          <Button 
            variant={showInactive ? "default" : "outline"}
            onClick={() => setShowInactive(!showInactive)} 
            className="flex items-center gap-2"
          >
            {showInactive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showInactive ? 'Απόκρυψη Ανενεργών' : 'Εμφάνιση Ανενεργών'}
          </Button>
          <Button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            {showForm ? 'Ακύρωση' : 'Νέο Αναλώσιμο'}
          </Button>
        </div>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Επεξεργασία Αναλωσίμου' : 'Νέο Αναλώσιμο'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>ΟΝΟΜΑ ΑΝΑΛΩΣΙΜΟΥ / ΑΝΤΑΛΛΑΚΤΙΚΟΥ *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Π.χ. TONER TK-1150"
                    required
                  />
                </div>
                <div>
                  <Label>ΜΑΡΚΑ</Label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="Π.χ. KYOCERA"
                  />
                </div>
                <div>
                  <Label>ΚΩΔΙΚΟΣ ΕΙΔΟΥΣ</Label>
                  <Input
                    value={formData.megasoft_code}
                    onChange={(e) => setFormData({ ...formData, megasoft_code: e.target.value })}
                    placeholder="Κωδικός Είδους"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>ΤΙΜΗ ΚΟΣΤΟΥΣ (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cost_price}
                    onChange={(e) => setFormData({ ...formData, cost_price: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>ΤΥΠΟΣ</Label>
                  <select
                    value={formData.consumable_type}
                    onChange={(e) => setFormData({ ...formData, consumable_type: e.target.value })}
                    className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-700"
                  >
                    <option value="ORIGINAL">ORIGINAL</option>
                    <option value="GENERIC">GENERIC</option>
                    <option value="USED">USED</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>ΚΑΤΗΓΟΡΙΑ</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-700"
                  >
                    <option value="ΑΝΑΛΩΣΙΜΟ">ΑΝΑΛΩΣΙΜΟ</option>
                    <option value="ΑΝΤΑΛΛΑΚΤΙΚΟ">ΑΝΤΑΛΛΑΚΤΙΚΟ</option>
                  </select>
                </div>
                {formData.category === 'ΑΝΑΛΩΣΙΜΟ' && (
                  <>
                    <div>
                      <Label>ΣΕΛΙΔΕΣ ΠΟΥ ΒΓΑΖΕΙ</Label>
                      <Input
                        type="number"
                        value={formData.pages_yield}
                        onChange={(e) => setFormData({ ...formData, pages_yield: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label>ΚΟΣΤΟΣ/ΣΕΛΙΔΑ (€)</Label>
                      <Input
                        value={calculateCostPerPage(formData.cost_price, formData.pages_yield)}
                        disabled
                        className="bg-gray-100 dark:bg-gray-800"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* COMPATIBLE MODELS */}
              <div className="border-t pt-4">
                <Label className="text-lg font-semibold">📋 ΣΥΜΒΑΤΑ ΜΟΝΤΕΛΑ</Label>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Επιλέξτε τα μοντέλα μηχανημάτων που είναι συμβατά με αυτό το αναλώσιμο
                </p>
                
                {catalog.length === 0 ? (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4 rounded">
                    <p className="text-yellow-800 dark:text-yellow-200">
                      ⚠️ ΔΕΝ ΥΠΑΡΧΟΥΝ ΜΟΝΤΕΛΑ ΣΤΟΝ ΚΑΤΑΛΟΓΟ. ΠΡΟΣΘΕΣΤΕ ΠΡΩΤΑ ΜΟΝΤΕΛΑ.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3 max-h-60 overflow-y-auto p-3 border rounded bg-gray-50 dark:bg-gray-900/50">
                    {catalog.map(model => (
                      <label key={model.id} className="flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedModels.includes(model.id)}
                          onChange={() => toggleModelSelection(model.id)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">
                          {model.brand} {model.model} ({model.machine_type})
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1">
                  {editingId ? '💾 ΑΠΟΘΗΚΕΥΣΗ ΑΛΛΑΓΩΝ' : '✅ ΑΠΟΘΗΚΕΥΣΗ'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Ακύρωση
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Φόρτωση...</div>
          ) : (
            <DataTable
              columns={columns}
              data={consumables}
              actions={(row) => (
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => handleToggleActive(row)}
                    title={row.is_active === 0 ? 'Ενεργοποίηση' : 'Απενεργοποίηση'}
                  >
                    {row.is_active === 0 ? (
                      <Power className="h-4 w-4 text-green-500" />
                    ) : (
                      <PowerOff className="h-4 w-4 text-orange-500" />
                    )}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(row)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(row.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              )}
            />
          )}
        </CardContent>
      </Card>
      
      {/* Password Modal */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPendingAction(null);
        }}
        onConfirm={handlePasswordConfirm}
        title="Επιβεβαίωση Κωδικού Admin"
      />
    </div>
  );
};

export default ConsumablesPage;
